import React from 'react';
import { 
  TrendingUp,
  BarChart3,
  Calendar,
  Activity
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import HeroStatsTiles from './HeroStatsTiles';
import { HeroStatTile } from '@/types/common';

interface ApiUsageHeroStatsProps {
  totalCalls: number;
  peakCalls: number;
  dataPoints: number;
  averageCalls: number;
  previousTotalCalls?: number;
  previousPeakCalls?: number;
  previousDataPoints?: number;
  previousAverageCalls?: number;
  loading?: boolean;
  className?: string;
}

const ApiUsageHeroStats: React.FC<ApiUsageHeroStatsProps> = ({ 
  totalCalls, 
  peakCalls, 
  dataPoints, 
  averageCalls,
  previousTotalCalls,
  previousPeakCalls,
  previousDataPoints,
  previousAverageCalls,
  loading = false, 
  className = '' 
}) => {
  // Calculate percentage changes
  const calculatePercentChange = (current: number, previous?: number) => {
    if (!previous || previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  };

  const totalCallsChange = calculatePercentChange(totalCalls, previousTotalCalls);
  const peakCallsChange = calculatePercentChange(peakCalls, previousPeakCalls);
  const dataPointsChange = calculatePercentChange(dataPoints, previousDataPoints);
  const averageCallsChange = calculatePercentChange(averageCalls, previousAverageCalls);

  const heroTiles: HeroStatTile[] = [
    {
      label: 'Total Calls',
      value: totalCalls.toLocaleString(),
      percentChange: totalCallsChange,
      percentChangeDirection: totalCallsChange > 0 ? 'up' : 'down',
      percentChangeColor: totalCallsChange > 0 ? 'green' : 'red',
      percentChangeLabel: `${totalCallsChange > 0 ? '+' : ''}${totalCallsChange.toFixed(1)}%`,
      icon: <TrendingUp className="text-blue-600 dark:text-blue-500" size={20} />,
    },
    {
      label: 'Peak Calls',
      value: peakCalls.toLocaleString(),
      percentChange: peakCallsChange,
      percentChangeDirection: peakCallsChange > 0 ? 'up' : 'down',
      percentChangeColor: peakCallsChange > 0 ? 'green' : 'red',
      percentChangeLabel: `${peakCallsChange > 0 ? '+' : ''}${peakCallsChange.toFixed(1)}%`,
      icon: <BarChart3 className="text-orange-600 dark:text-orange-500" size={20} />,
    },
    {
      label: 'Data Points',
      value: dataPoints.toString(),
      percentChange: dataPointsChange,
      percentChangeDirection: dataPointsChange > 0 ? 'up' : 'down',
      percentChangeColor: dataPointsChange > 0 ? 'green' : 'red',
      percentChangeLabel: `${dataPointsChange > 0 ? '+' : ''}${dataPointsChange.toFixed(1)}%`,
      icon: <Calendar className="text-green-600 dark:text-green-500" size={20} />,
    },
    {
      label: 'Average Calls',
      value: averageCalls.toLocaleString(),
      percentChange: averageCallsChange,
      percentChangeDirection: averageCallsChange > 0 ? 'up' : 'down',
      percentChangeColor: averageCallsChange > 0 ? 'green' : 'red',
      percentChangeLabel: `${averageCallsChange > 0 ? '+' : ''}${averageCallsChange.toFixed(1)}%`,
      icon: <Activity className="text-purple-600 dark:text-purple-500" size={20} />,
    },
  ];

  if (loading) {
    return (
      <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 ${className}`}>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-2 mb-2">
              <Skeleton className="w-5 h-5 rounded" />
              <Skeleton className="h-4 w-20" />
            </div>
            <Skeleton className="h-8 w-16 mb-2" />
            <Skeleton className="h-3 w-12" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <HeroStatsTiles 
      tiles={heroTiles} 
      className={className}
    />
  );
};

export default ApiUsageHeroStats; 